import React, { useState, useEffect, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  AcademicCapIcon,
  BanknotesIcon,
  UserGroupIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  SparklesIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  PlusIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CheckIcon,
  XMarkIcon,
  ClockIcon,
  BuildingLibraryIcon,
  TruckIcon,
  BookOpenIcon,
  BeakerIcon,
  ComputerDesktopIcon,
  CakeIcon,
  ShieldCheckIcon,
  TagIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  InformationCircleIcon,
  RocketLaunchIcon,
  DocumentDuplicateIcon,
  EyeIcon,
} from '@heroicons/react/24/outline'
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import toast from 'react-hot-toast'
import { apiClient } from '@/api/client'
import { getGrades } from '@/api/academicApi'
import { getServices, createService, BillableService, ServiceCategory, PricingType } from '@/api/billingApi'

// ============================================================================
// TYPES
// ============================================================================

interface Grade {
  _id: string
  name: string
  level: number
  streams: Stream[]
}

interface Stream {
  _id: string
  name: string
  studentCount: number
}

interface FeeItem {
  id: string
  name: string
  category: FeeCategory
  amount: number
  baseAmount: number // Original amount from service
  isOptional: boolean
  description?: string
  serviceId: string // Reference to BillableService ID
  pricingType: 'FIXED' | 'VARIABLE'
  minPrice?: number
  maxPrice?: number
}

interface SelectedGrade {
  gradeId: string
  gradeName: string
  streams: {
    streamId: string
    streamName: string
    studentCount: number
    selected: boolean
  }[]
  allSelected: boolean
}

interface GenerationPreview {
  totalStudents: number
  totalAmount: number
  byGrade: {
    gradeId: string
    gradeName: string
    studentCount: number
    amount: number
  }[]
  feeBreakdown: {
    name: string
    amount: number
    total: number
  }[]
}

interface GenerationResult {
  success: boolean
  invoicesCreated: number
  totalAmount: number
  errors: { studentId: string; studentName: string; error: string }[]
}

type FeeCategory =
  | 'TUITION'
  | 'BOARDING'
  | 'TRANSPORT'
  | 'MEALS'
  | 'UNIFORM'
  | 'BOOKS'
  | 'ACTIVITIES'
  | 'EXAM_FEES'
  | 'LAB_FEES'
  | 'LIBRARY'
  | 'TECHNOLOGY'
  | 'INSURANCE'
  | 'OTHER'

type WizardStep = 'term' | 'fees' | 'students' | 'preview' | 'complete'

// ============================================================================
// CONSTANTS
// ============================================================================

const STEPS: { id: WizardStep; title: string; icon: React.ElementType }[] = [
  { id: 'term', title: 'Select Term', icon: CalendarIcon },
  { id: 'fees', title: 'Select Fees', icon: BanknotesIcon },
  { id: 'students', title: 'Select Students', icon: UserGroupIcon },
  { id: 'preview', title: 'Generate', icon: RocketLaunchIcon },
]

const FEE_CATEGORIES: { value: FeeCategory; label: string; icon: React.ElementType; color: string }[] = [
  { value: 'TUITION', label: 'Tuition Fees', icon: AcademicCapIcon, color: 'blue' },
  { value: 'BOARDING', label: 'Boarding', icon: BuildingLibraryIcon, color: 'purple' },
  { value: 'TRANSPORT', label: 'Transport', icon: TruckIcon, color: 'green' },
  { value: 'MEALS', label: 'Meals', icon: CakeIcon, color: 'orange' },
  { value: 'BOOKS', label: 'Books & Materials', icon: BookOpenIcon, color: 'indigo' },
  { value: 'UNIFORM', label: 'Uniform', icon: TagIcon, color: 'pink' },
  { value: 'ACTIVITIES', label: 'Activities', icon: SparklesIcon, color: 'yellow' },
  { value: 'EXAM_FEES', label: 'Exam Fees', icon: DocumentTextIcon, color: 'red' },
  { value: 'LAB_FEES', label: 'Lab Fees', icon: BeakerIcon, color: 'cyan' },
  { value: 'LIBRARY', label: 'Library', icon: BookOpenIcon, color: 'emerald' },
  { value: 'TECHNOLOGY', label: 'Technology', icon: ComputerDesktopIcon, color: 'slate' },
  { value: 'INSURANCE', label: 'Insurance', icon: ShieldCheckIcon, color: 'teal' },
  { value: 'OTHER', label: 'Other', icon: CurrencyDollarIcon, color: 'gray' },
]

const TERMS = [
  { id: 'term1', name: 'Term 1', period: 'January - April' },
  { id: 'term2', name: 'Term 2', period: 'May - August' },
  { id: 'term3', name: 'Term 3', period: 'September - December' },
]

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency: 'KES',
    minimumFractionDigits: 0,
  }).format(amount)
}

const getCategoryConfig = (category: FeeCategory) => {
  return FEE_CATEGORIES.find(c => c.value === category) || FEE_CATEGORIES[FEE_CATEGORIES.length - 1]
}

const generateId = () => Math.random().toString(36).substring(2, 9)

// ============================================================================
// PREVIEW SIDEBAR COMPONENT
// ============================================================================

const PreviewSidebar: React.FC<{
  selectedTerm: string
  academicYear: string
  feeItems: FeeItem[]
  selectedGrades: SelectedGrade[]
  currentStep: WizardStep
}> = ({ selectedTerm, academicYear, feeItems, selectedGrades, currentStep }) => {
  const termName = TERMS.find(t => t.id === selectedTerm)?.name || 'Not selected'

  const totalFeePerStudent = feeItems.reduce((sum, item) => sum + item.amount, 0)

  const totalSelectedStudents = selectedGrades.reduce((sum, grade) => {
    return sum + grade.streams.filter(s => s.selected).reduce((sSum, s) => sSum + s.studentCount, 0)
  }, 0)

  const totalAmount = totalFeePerStudent * totalSelectedStudents

  const selectedGradesList = selectedGrades.filter(g => g.streams.some(s => s.selected))

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 sticky top-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-t-2xl p-4 text-white">
        <div className="flex items-center gap-2 mb-1">
          <EyeIcon className="w-5 h-5" />
          <h3 className="font-semibold">Invoice Preview</h3>
        </div>
        <p className="text-blue-100 text-sm">Real-time calculation</p>
      </div>

      <div className="p-4 space-y-4">
        {/* Period Info */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Academic Year</span>
            <span className="font-medium text-gray-900">{academicYear}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Term</span>
            <span className={`font-medium ${selectedTerm ? 'text-gray-900' : 'text-gray-400'}`}>
              {termName}
            </span>
          </div>
        </div>

        <hr className="border-gray-100" />

        {/* Fee Items Summary */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Fee Items</span>
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
              {feeItems.length}
            </span>
          </div>
          {feeItems.length > 0 ? (
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {feeItems.map(item => (
                <div key={item.id} className="flex justify-between text-xs py-1">
                  <span className="text-gray-600 truncate max-w-[120px]">{item.name}</span>
                  <span className="font-medium text-gray-900">{formatCurrency(item.amount)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400 italic">No fees selected</p>
          )}
          {feeItems.length > 0 && (
            <div className="flex justify-between text-sm font-medium pt-2 mt-2 border-t border-gray-100">
              <span className="text-gray-700">Fee per Student</span>
              <span className="text-blue-600">{formatCurrency(totalFeePerStudent)}</span>
            </div>
          )}
        </div>

        <hr className="border-gray-100" />

        {/* Students Summary */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Students</span>
            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
              {totalSelectedStudents}
            </span>
          </div>
          {selectedGradesList.length > 0 ? (
            <div className="space-y-1 max-h-24 overflow-y-auto">
              {selectedGradesList.map(grade => {
                const count = grade.streams.filter(s => s.selected).reduce((sum, s) => sum + s.studentCount, 0)
                return (
                  <div key={grade.gradeId} className="flex justify-between text-xs py-1">
                    <span className="text-gray-600">{grade.gradeName}</span>
                    <span className="text-gray-900">{count} students</span>
                  </div>
                )
              })}
            </div>
          ) : (
            <p className="text-xs text-gray-400 italic">No students selected</p>
          )}
        </div>

        <hr className="border-gray-100" />

        {/* Total */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-green-600 font-medium">Total Invoice Amount</p>
              <p className="text-2xl font-bold text-green-700">{formatCurrency(totalAmount)}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-green-600">{totalSelectedStudents} invoices</p>
            </div>
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="pt-2">
          <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
            <span>Progress</span>
            <span>{STEPS.findIndex(s => s.id === currentStep) + 1}/{STEPS.length}</span>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-blue-500 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${((STEPS.findIndex(s => s.id === currentStep) + 1) / STEPS.length) * 100}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// STEP COMPONENTS
// ============================================================================

// Step Indicator
const StepIndicator: React.FC<{ currentStep: WizardStep }> = ({ currentStep }) => {
  const currentIndex = STEPS.findIndex(s => s.id === currentStep)

  return (
    <div className="flex items-center justify-between mb-6 px-2">
      {STEPS.map((step, index) => {
        const isCompleted = index < currentIndex
        const isCurrent = index === currentIndex
        const Icon = step.icon

        return (
          <React.Fragment key={step.id}>
            <div className="flex flex-col items-center">
              <div
                className={`
                  w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300
                  ${isCompleted ? 'bg-green-500 text-white' : ''}
                  ${isCurrent ? 'bg-blue-600 text-white ring-4 ring-blue-100' : ''}
                  ${!isCompleted && !isCurrent ? 'bg-gray-100 text-gray-400' : ''}
                `}
              >
                {isCompleted ? (
                  <CheckIcon className="w-5 h-5" />
                ) : (
                  <Icon className="w-5 h-5" />
                )}
              </div>
              <span
                className={`
                  mt-1.5 text-xs font-medium
                  ${isCurrent ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-gray-400'}
                `}
              >
                {step.title}
              </span>
            </div>
            {index < STEPS.length - 1 && (
              <div
                className={`
                  flex-1 h-0.5 mx-2 rounded transition-all duration-300
                  ${index < currentIndex ? 'bg-green-500' : 'bg-gray-200'}
                `}
              />
            )}
          </React.Fragment>
        )
      })}
    </div>
  )
}

// Term Selection Step
const TermSelectionStep: React.FC<{
  selectedTerm: string
  academicYear: string
  onSelectTerm: (term: string) => void
  onChangeYear: (year: string) => void
}> = ({ selectedTerm, academicYear, onSelectTerm, onChangeYear }) => {
  const currentYear = new Date().getFullYear()
  const years = [currentYear - 1, currentYear, currentYear + 1]

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div>
        <h2 className="text-xl font-bold text-gray-900">Select Academic Period</h2>
        <p className="text-gray-500 text-sm mt-1">Choose the term and year for fee generation</p>
      </div>

      {/* Academic Year */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Academic Year</label>
        <div className="flex gap-2">
          {years.map(year => (
            <button
              key={year}
              onClick={() => onChangeYear(year.toString())}
              className={`
                px-5 py-2.5 rounded-lg font-medium transition-all duration-200
                ${academicYear === year.toString()
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-200'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }
              `}
            >
              {year}
            </button>
          ))}
        </div>
      </div>

      {/* Term Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Select Term</label>
        <div className="grid grid-cols-3 gap-3">
          {TERMS.map((term) => (
            <button
              key={term.id}
              onClick={() => onSelectTerm(term.id)}
              className={`
                relative p-4 rounded-xl border-2 transition-all duration-200 text-left
                ${selectedTerm === term.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
                }
              `}
            >
              {selectedTerm === term.id && (
                <CheckCircleSolidIcon className="w-5 h-5 text-blue-500 absolute top-2 right-2" />
              )}
              <CalendarIcon className={`w-6 h-6 mb-2 ${selectedTerm === term.id ? 'text-blue-500' : 'text-gray-400'}`} />
              <h3 className="font-semibold text-gray-900 text-sm">{term.name}</h3>
              <p className="text-xs text-gray-500">{term.period}</p>
            </button>
          ))}
        </div>
      </div>
    </motion.div>
  )
}

// Fee Selection Step - Fetches billable services from API with fixed/variable pricing
const FeeSelectionStep: React.FC<{
  feeItems: FeeItem[]
  onAddItem: (item: FeeItem) => void
  onRemoveItem: (id: string) => void
  onUpdateItem: (id: string, updates: Partial<FeeItem>) => void
}> = ({ feeItems, onAddItem, onRemoveItem, onUpdateItem }) => {
  const [availableServices, setAvailableServices] = useState<BillableService[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [showCustomModal, setShowCustomModal] = useState(false)
  const [customSaving, setCustomSaving] = useState(false)
  const [customFee, setCustomFee] = useState({
    name: '',
    category: 'OTHER' as FeeCategory,
    amount: 0,
    description: '',
    pricingType: 'FIXED' as 'FIXED' | 'VARIABLE',
    minPrice: 0,
    maxPrice: 0,
  })
  const [editingFeeId, setEditingFeeId] = useState<string | null>(null)
  const [editAmount, setEditAmount] = useState<number>(0)

  useEffect(() => {
    fetchAvailableServices()
  }, [])

  const fetchAvailableServices = async () => {
    setLoading(true)
    try {
      const response = await getServices({ isActive: true })
      setAvailableServices(response.data?.services || response.services || [])
    } catch (error) {
      console.error('Failed to fetch services:', error)
      toast.error('Failed to load billable services')
    } finally {
      setLoading(false)
    }
  }

  const filteredServices = availableServices.filter(service => {
    if (!service || !service.name) return false
    const matchesSearch = service.name.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = categoryFilter === 'all' || service.category === categoryFilter
    return matchesSearch && matchesCategory
  })

  const isSelected = (serviceId: string) => feeItems.some(f => f.serviceId === serviceId)

  const handleToggleService = (service: BillableService) => {
    if (isSelected(service._id)) {
      const item = feeItems.find(f => f.serviceId === service._id)
      if (item) onRemoveItem(item.id)
    } else {
      const amount = service.baseAmount || service.basePrice || 0
      onAddItem({
        id: generateId(),
        serviceId: service._id,
        name: service.name,
        category: service.category as FeeCategory,
        amount: amount,
        baseAmount: amount,
        isOptional: service.optional,
        description: service.description,
        pricingType: service.pricingType || 'FIXED',
        minPrice: service.minPrice,
        maxPrice: service.maxPrice,
      })
    }
  }

  const handleAddCustomService = async () => {
    if (!customFee.name || !customFee.amount) {
      toast.error('Please fill in name and amount')
      return
    }

    setCustomSaving(true)
    try {
      // Create the service in the backend
      const response = await createService({
        name: customFee.name,
        description: customFee.description,
        category: customFee.category as ServiceCategory,
        billingFrequency: 'TERMLY',
        baseAmount: customFee.amount,
        basePrice: customFee.amount, // Backend expects basePrice
        pricingType: customFee.pricingType,
        minPrice: customFee.pricingType === 'VARIABLE' ? customFee.minPrice : undefined,
        maxPrice: customFee.pricingType === 'VARIABLE' ? customFee.maxPrice : undefined,
        applicability: 'ALL',
      })

      const newService = response.data?.service || response.data || response

      // Add to selected fees
      onAddItem({
        id: generateId(),
        serviceId: newService._id,
        name: newService.name,
        category: newService.category as FeeCategory,
        amount: customFee.amount,
        baseAmount: customFee.amount,
        isOptional: false,
        description: customFee.description,
        pricingType: customFee.pricingType,
        minPrice: customFee.minPrice || undefined,
        maxPrice: customFee.maxPrice || undefined,
      })

      setCustomFee({ name: '', category: 'OTHER', amount: 0, description: '', pricingType: 'FIXED', minPrice: 0, maxPrice: 0 })
      setShowCustomModal(false)
      toast.success('Service created and added to invoice')

      // Refresh the services list from API
      await fetchAvailableServices()
    } catch (error: any) {
      console.error('Failed to create service:', error)
      toast.error(error.response?.data?.message || 'Failed to create service')
    } finally {
      setCustomSaving(false)
    }
  }

  const handleEditAmount = (item: FeeItem) => {
    if (item.pricingType === 'VARIABLE') {
      setEditingFeeId(item.id)
      setEditAmount(item.amount)
    }
  }

  const handleSaveAmount = (item: FeeItem) => {
    // Validate against min/max
    let finalAmount = editAmount
    if (item.minPrice !== undefined && editAmount < item.minPrice) {
      finalAmount = item.minPrice
      toast.error(`Minimum amount is ${formatCurrency(item.minPrice)}`)
    }
    if (item.maxPrice !== undefined && editAmount > item.maxPrice) {
      finalAmount = item.maxPrice
      toast.error(`Maximum amount is ${formatCurrency(item.maxPrice)}`)
    }

    onUpdateItem(item.id, { amount: finalAmount })
    setEditingFeeId(null)
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
        <p className="text-gray-500 mt-3 text-sm">Loading billable services...</p>
      </div>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-4"
    >
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Select Services</h2>
          <p className="text-gray-500 text-sm">Choose billable services for the invoice</p>
        </div>
        <button
          onClick={() => setShowCustomModal(true)}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
        >
          <PlusIcon className="w-4 h-4" />
          Add New Service
        </button>
      </div>

      {/* Search and Filter */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search services..."
            className="pl-9 h-9"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Categories</option>
          {FEE_CATEGORIES.map(cat => (
            <option key={cat.value} value={cat.value}>{cat.label}</option>
          ))}
        </select>
      </div>

      {/* Available Services Grid */}
      <div className="grid grid-cols-2 gap-2 max-h-[280px] overflow-y-auto pr-1">
        {filteredServices.map(service => {
          const selected = isSelected(service._id)
          const categoryConfig = getCategoryConfig(service.category as FeeCategory)
          const Icon = categoryConfig.icon
          const isVariable = service.pricingType === 'VARIABLE'
          const amount = service.baseAmount || service.basePrice || 0

          return (
            <motion.button
              key={service._id}
              onClick={() => handleToggleService(service)}
              whileTap={{ scale: 0.98 }}
              className={`
                p-3 rounded-lg border-2 text-left transition-all relative
                ${selected
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
                }
              `}
            >
              <div className="flex items-start justify-between mb-1">
                <div className={`p-1.5 rounded bg-${categoryConfig.color}-100`}>
                  <Icon className={`w-4 h-4 text-${categoryConfig.color}-600`} />
                </div>
                <div className="flex items-center gap-1">
                  {isVariable && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-medium">
                      Variable
                    </span>
                  )}
                  <div className={`
                    w-5 h-5 rounded border-2 flex items-center justify-center
                    ${selected ? 'bg-blue-500 border-blue-500' : 'border-gray-300'}
                  `}>
                    {selected && <CheckIcon className="w-3 h-3 text-white" />}
                  </div>
                </div>
              </div>
              <h4 className="font-medium text-gray-900 text-sm truncate">{service.name}</h4>
              <p className="text-lg font-bold text-gray-900">{formatCurrency(amount)}</p>
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">{categoryConfig.label}</p>
                {isVariable && service.minPrice !== undefined && service.maxPrice !== undefined && (
                  <p className="text-[10px] text-gray-400">
                    {formatCurrency(service.minPrice)} - {formatCurrency(service.maxPrice)}
                  </p>
                )}
              </div>
            </motion.button>
          )
        })}
      </div>

      {filteredServices.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <BanknotesIcon className="w-10 h-10 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">No billable services found</p>
          <button
            onClick={() => setShowCustomModal(true)}
            className="text-blue-600 text-sm mt-2 hover:underline"
          >
            Add a new service
          </button>
        </div>
      )}

      {/* Selected Services Summary with Editable Prices */}
      {feeItems.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Selected Services ({feeItems.length})</span>
            <span className="text-sm font-bold text-blue-600">
              {formatCurrency(feeItems.reduce((sum, f) => sum + f.amount, 0))} / student
            </span>
          </div>
          <div className="space-y-2">
            {feeItems.map(item => (
              <div
                key={item.id}
                className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-gray-200"
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <span className="text-sm text-gray-900 truncate">{item.name}</span>
                  {item.pricingType === 'VARIABLE' && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-medium flex-shrink-0">
                      Variable
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {editingFeeId === item.id ? (
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        value={editAmount}
                        onChange={(e) => setEditAmount(Number(e.target.value))}
                        className="w-24 px-2 py-1 text-sm border border-blue-300 rounded focus:ring-2 focus:ring-blue-500"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveAmount(item)
                          if (e.key === 'Escape') setEditingFeeId(null)
                        }}
                      />
                      <button
                        onClick={() => handleSaveAmount(item)}
                        className="p-1 text-green-600 hover:bg-green-50 rounded"
                      >
                        <CheckIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setEditingFeeId(null)}
                        className="p-1 text-gray-400 hover:bg-gray-100 rounded"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <span className={`text-sm font-medium ${item.amount !== item.baseAmount ? 'text-amber-600' : 'text-gray-900'}`}>
                        {formatCurrency(item.amount)}
                      </span>
                      {item.pricingType === 'VARIABLE' && (
                        <button
                          onClick={() => handleEditAmount(item)}
                          className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                          title="Edit amount"
                        >
                          <CurrencyDollarIcon className="w-4 h-4" />
                        </button>
                      )}
                    </>
                  )}
                  <button
                    onClick={() => onRemoveItem(item.id)}
                    className="p-1 text-red-500 hover:bg-red-50 rounded"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add New Service Modal */}
      <AnimatePresence>
        {showCustomModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowCustomModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl p-5 w-full max-w-md"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Add New Billable Service</h3>
              <p className="text-xs text-gray-500 mb-4">
                This will create a new service in your institution's billing system.
              </p>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Service Name *</label>
                  <Input
                    value={customFee.name}
                    onChange={(e) => setCustomFee({ ...customFee, name: e.target.value })}
                    placeholder="e.g., Development Levy"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={customFee.category}
                    onChange={(e) => setCustomFee({ ...customFee, category: e.target.value as FeeCategory })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    {FEE_CATEGORIES.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pricing Type</label>
                  <div className="flex gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="pricingType"
                        checked={customFee.pricingType === 'FIXED'}
                        onChange={() => setCustomFee({ ...customFee, pricingType: 'FIXED' })}
                        className="text-blue-600"
                      />
                      <span className="text-sm text-gray-700">Fixed</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="pricingType"
                        checked={customFee.pricingType === 'VARIABLE'}
                        onChange={() => setCustomFee({ ...customFee, pricingType: 'VARIABLE' })}
                        className="text-blue-600"
                      />
                      <span className="text-sm text-gray-700">Variable</span>
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {customFee.pricingType === 'FIXED'
                      ? 'Price cannot be changed during invoicing'
                      : 'Price can be adjusted within limits during invoicing'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount (KES) *</label>
                  <Input
                    type="number"
                    value={customFee.amount || ''}
                    onChange={(e) => setCustomFee({ ...customFee, amount: Number(e.target.value) })}
                    placeholder="0"
                  />
                </div>
                {customFee.pricingType === 'VARIABLE' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Min Price</label>
                      <Input
                        type="number"
                        value={customFee.minPrice || ''}
                        onChange={(e) => setCustomFee({ ...customFee, minPrice: Number(e.target.value) })}
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Max Price</label>
                      <Input
                        type="number"
                        value={customFee.maxPrice || ''}
                        onChange={(e) => setCustomFee({ ...customFee, maxPrice: Number(e.target.value) })}
                        placeholder="0"
                      />
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <Input
                    value={customFee.description}
                    onChange={(e) => setCustomFee({ ...customFee, description: e.target.value })}
                    placeholder="Optional description"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-5">
                <Button variant="secondary" className="flex-1" onClick={() => setShowCustomModal(false)}>
                  Cancel
                </Button>
                <Button className="flex-1" onClick={handleAddCustomService} loading={customSaving}>
                  Create & Add
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

// Student Selection Step
const StudentSelectionStep: React.FC<{
  grades: Grade[]
  selectedGrades: SelectedGrade[]
  onToggleGrade: (gradeId: string) => void
  onToggleStream: (gradeId: string, streamId: string) => void
  onSelectAll: () => void
  onClearAll: () => void
  loading: boolean
}> = ({ grades, selectedGrades, onToggleGrade, onToggleStream, onSelectAll, onClearAll, loading }) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedGrades, setExpandedGrades] = useState<Set<string>>(new Set())

  const toggleGradeExpansion = (gradeId: string) => {
    setExpandedGrades(prev => {
      const newSet = new Set(prev)
      if (newSet.has(gradeId)) {
        newSet.delete(gradeId)
      } else {
        newSet.add(gradeId)
      }
      return newSet
    })
  }

  const totalSelected = selectedGrades.reduce((sum, grade) => {
    return sum + grade.streams.filter(s => s.selected).reduce((sSum, s) => sSum + s.studentCount, 0)
  }, 0)

  const filteredGrades = grades.filter(grade =>
    grade.name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
        <p className="text-gray-500 mt-3 text-sm">Loading grades...</p>
      </div>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-4"
    >
      <div>
        <h2 className="text-xl font-bold text-gray-900">Select Students</h2>
        <p className="text-gray-500 text-sm">Choose grades and streams for invoice generation</p>
      </div>

      {/* Search and Actions */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Filter grades..."
            className="pl-9 h-9"
          />
        </div>
        <Button variant="secondary" size="sm" onClick={onSelectAll}>All</Button>
        <Button variant="secondary" size="sm" onClick={onClearAll}>Clear</Button>
      </div>

      {/* Selected Count Badge */}
      <div className="bg-blue-50 rounded-lg p-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <UserGroupIcon className="w-5 h-5 text-blue-600" />
          <span className="text-sm font-medium text-blue-900">Students Selected</span>
        </div>
        <span className="text-2xl font-bold text-blue-600">{totalSelected}</span>
      </div>

      {/* Grade List */}
      <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
        {filteredGrades.map((grade) => {
          const selectedGrade = selectedGrades.find(g => g.gradeId === grade._id)
          const isExpanded = expandedGrades.has(grade._id)
          const totalStudents = grade.streams.reduce((sum, s) => sum + s.studentCount, 0)
          const selectedStudents = selectedGrade?.streams
            .filter(s => s.selected)
            .reduce((sum, s) => sum + s.studentCount, 0) || 0
          const allStreamsSelected = selectedGrade?.allSelected || false
          const someStreamsSelected = selectedStudents > 0 && !allStreamsSelected

          return (
            <div key={grade._id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="p-3 flex items-center justify-between hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); onToggleGrade(grade._id) }}
                    className={`
                      w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0
                      ${allStreamsSelected ? 'bg-blue-500 border-blue-500'
                        : someStreamsSelected ? 'bg-blue-200 border-blue-400'
                        : 'border-gray-300 hover:border-blue-400'}
                    `}
                  >
                    {allStreamsSelected && <CheckIcon className="w-3 h-3 text-white" />}
                    {someStreamsSelected && !allStreamsSelected && (
                      <div className="w-2 h-2 bg-blue-500 rounded-sm" />
                    )}
                  </button>

                  <button
                    onClick={() => toggleGradeExpansion(grade._id)}
                    className="flex items-center gap-2"
                  >
                    <div className={`
                      w-8 h-8 rounded flex items-center justify-center font-bold text-sm
                      ${allStreamsSelected ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600'}
                    `}>
                      {grade.name.charAt(0)}
                    </div>
                    <div className="text-left">
                      <h4 className="font-medium text-gray-900 text-sm">{grade.name}</h4>
                      <p className="text-xs text-gray-500">{totalStudents} students</p>
                    </div>
                  </button>
                </div>

                <button
                  onClick={() => toggleGradeExpansion(grade._id)}
                  className="flex items-center gap-2"
                >
                  {selectedStudents > 0 && (
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                      {selectedStudents}
                    </span>
                  )}
                  <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </button>
              </div>

              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="border-t border-gray-100 bg-gray-50"
                  >
                    <div className="p-2 space-y-1">
                      {grade.streams.length > 0 ? (
                        grade.streams.map(stream => {
                          const selectedStream = selectedGrade?.streams.find(s => s.streamId === stream._id)
                          const isSelected = selectedStream?.selected || false

                          return (
                            <button
                              key={stream._id}
                              onClick={(e) => { e.stopPropagation(); onToggleStream(grade._id, stream._id) }}
                              className={`
                                w-full p-2 rounded flex items-center justify-between text-sm transition-all
                                ${isSelected ? 'bg-blue-100 border border-blue-300' : 'bg-white border border-gray-200 hover:border-gray-300'}
                              `}
                            >
                              <div className="flex items-center gap-2">
                                <div className={`
                                  w-4 h-4 rounded border-2 flex items-center justify-center
                                  ${isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300'}
                                `}>
                                  {isSelected && <CheckIcon className="w-2.5 h-2.5 text-white" />}
                                </div>
                                <span className="font-medium text-gray-700">{stream.name}</span>
                              </div>
                              <span className="text-xs text-gray-500">{stream.studentCount}</span>
                            </button>
                          )
                        })
                      ) : (
                        <p className="text-center py-2 text-gray-500 text-xs">No streams</p>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )
        })}
      </div>
    </motion.div>
  )
}

// Generate/Preview Step
const GenerateStep: React.FC<{
  feeItems: FeeItem[]
  selectedGrades: SelectedGrade[]
  selectedTerm: string
  academicYear: string
  onGenerate: () => void
  generating: boolean
}> = ({ feeItems, selectedGrades, selectedTerm, academicYear, onGenerate, generating }) => {
  const termName = TERMS.find(t => t.id === selectedTerm)?.name || selectedTerm
  const totalFeePerStudent = feeItems.reduce((sum, item) => sum + item.amount, 0)

  const byGrade = selectedGrades
    .filter(g => g.streams.some(s => s.selected))
    .map(grade => {
      const studentCount = grade.streams
        .filter(s => s.selected)
        .reduce((sum, s) => sum + s.studentCount, 0)
      return {
        gradeName: grade.gradeName,
        studentCount,
        amount: studentCount * totalFeePerStudent,
      }
    })

  const totalStudents = byGrade.reduce((sum, g) => sum + g.studentCount, 0)
  const totalAmount = totalStudents * totalFeePerStudent

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-4"
    >
      <div>
        <h2 className="text-xl font-bold text-gray-900">Ready to Generate</h2>
        <p className="text-gray-500 text-sm">Review and generate invoices</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-blue-50 rounded-lg p-3 text-center">
          <DocumentDuplicateIcon className="w-6 h-6 mx-auto mb-1 text-blue-600" />
          <p className="text-2xl font-bold text-blue-700">{totalStudents}</p>
          <p className="text-xs text-blue-600">Invoices</p>
        </div>
        <div className="bg-green-50 rounded-lg p-3 text-center">
          <BanknotesIcon className="w-6 h-6 mx-auto mb-1 text-green-600" />
          <p className="text-lg font-bold text-green-700">{formatCurrency(totalAmount)}</p>
          <p className="text-xs text-green-600">Total</p>
        </div>
        <div className="bg-purple-50 rounded-lg p-3 text-center">
          <CalendarIcon className="w-6 h-6 mx-auto mb-1 text-purple-600" />
          <p className="text-sm font-bold text-purple-700">{termName}</p>
          <p className="text-xs text-purple-600">{academicYear}</p>
        </div>
      </div>

      {/* Fee Breakdown */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-3 border-b border-gray-100 bg-gray-50">
          <h3 className="font-medium text-gray-900 text-sm">Fee Breakdown</h3>
        </div>
        <div className="divide-y divide-gray-100 max-h-32 overflow-y-auto">
          {feeItems.map(fee => (
            <div key={fee.id} className="p-3 flex justify-between text-sm">
              <span className="text-gray-600">{fee.name}</span>
              <span className="font-medium">{formatCurrency(fee.amount)}</span>
            </div>
          ))}
        </div>
        <div className="p-3 bg-gray-900 flex justify-between">
          <span className="text-gray-300 text-sm">Per Student</span>
          <span className="font-bold text-white">{formatCurrency(totalFeePerStudent)}</span>
        </div>
      </div>

      {/* By Grade */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-3 border-b border-gray-100 bg-gray-50">
          <h3 className="font-medium text-gray-900 text-sm">By Grade</h3>
        </div>
        <div className="divide-y divide-gray-100 max-h-24 overflow-y-auto">
          {byGrade.map((grade, idx) => (
            <div key={idx} className="p-2 flex justify-between items-center text-sm">
              <div>
                <span className="font-medium text-gray-900">{grade.gradeName}</span>
                <span className="text-gray-500 ml-2">({grade.studentCount})</span>
              </div>
              <span className="font-medium text-gray-900">{formatCurrency(grade.amount)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Generate Button */}
      <motion.button
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
        onClick={onGenerate}
        disabled={generating}
        className={`
          w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all
          ${generating
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 shadow-lg'
          }
        `}
      >
        {generating ? (
          <>
            <div className="w-4 h-4 border-2 border-gray-400 border-t-white rounded-full animate-spin" />
            Generating...
          </>
        ) : (
          <>
            <RocketLaunchIcon className="w-5 h-5" />
            Generate {totalStudents} Invoices
          </>
        )}
      </motion.button>
    </motion.div>
  )
}

// Complete Step
const CompleteStep: React.FC<{
  result: GenerationResult | null
  onReset: () => void
  onViewInvoices: () => void
}> = ({ result, onReset, onViewInvoices }) => {
  if (!result) return null

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="text-center py-6"
    >
      {result.success ? (
        <>
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200, damping: 15 }}
            className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"
          >
            <CheckCircleSolidIcon className="w-10 h-10 text-green-500" />
          </motion.div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Success!</h2>
          <p className="text-gray-500 text-sm mb-6">
            {result.invoicesCreated} invoices ({formatCurrency(result.totalAmount)}) created.
          </p>

          {result.errors.length > 0 && (
            <div className="mb-4 bg-yellow-50 rounded-lg p-3 text-left">
              <p className="text-yellow-700 text-sm font-medium mb-1">
                {result.errors.length} students could not be processed
              </p>
              <div className="text-xs text-yellow-600 max-h-20 overflow-y-auto">
                {result.errors.slice(0, 3).map((err, idx) => (
                  <p key={idx}>{err.studentName}: {err.error}</p>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2 justify-center">
            <Button variant="secondary" size="sm" onClick={onReset}>
              Generate More
            </Button>
            <Button size="sm" onClick={onViewInvoices}>
              <EyeIcon className="w-4 h-4 mr-1" />
              View Invoices
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XMarkIcon className="w-10 h-10 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Failed</h2>
          <p className="text-gray-500 text-sm mb-4">Something went wrong. Please try again.</p>
          <Button onClick={onReset}>Try Again</Button>
        </>
      )}
    </motion.div>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const FeeGenerationPage: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<WizardStep>('term')
  const [selectedTerm, setSelectedTerm] = useState<string>('')
  const [academicYear, setAcademicYear] = useState<string>(new Date().getFullYear().toString())
  const [feeItems, setFeeItems] = useState<FeeItem[]>([])
  const [grades, setGrades] = useState<Grade[]>([])
  const [selectedGrades, setSelectedGrades] = useState<SelectedGrade[]>([])
  const [gradesLoading, setGradesLoading] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [result, setResult] = useState<GenerationResult | null>(null)

  useEffect(() => {
    fetchGrades()
  }, [])

  const fetchGrades = async () => {
    setGradesLoading(true)
    try {
      const response = await getGrades({ limit: 100, status: 'active' })
      const gradesData = response.grades || []

      const gradesWithStreams: Grade[] = gradesData.map((grade) => ({
        _id: grade._id || grade.id,
        name: grade.name,
        level: 0,
        streams: (grade.streams || []).map((stream) => ({
          _id: stream._id || stream.id,
          name: stream.name,
          studentCount: stream.studentCount || stream.currentStudents || 0,
        })),
      }))

      setGrades(gradesWithStreams)
      setSelectedGrades(gradesWithStreams.map((grade: Grade) => ({
        gradeId: grade._id,
        gradeName: grade.name,
        streams: (grade.streams || []).map((stream: Stream) => ({
          streamId: stream._id,
          streamName: stream.name,
          studentCount: stream.studentCount || 0,
          selected: false,
        })),
        allSelected: false,
      })))
    } catch (error) {
      console.error('Error fetching grades:', error)
      toast.error('Failed to load grades')
    } finally {
      setGradesLoading(false)
    }
  }

  const handleGenerate = async () => {
    setGenerating(true)
    try {
      const selectedStreams = selectedGrades
        .flatMap(g => g.streams.filter(s => s.selected).map(s => s.streamId))

      const response = await apiClient.post('/invoices/bulk-generate', {
        term: selectedTerm,
        academicYear,
        streamIds: selectedStreams,
        feeItems: feeItems.map(item => ({
          name: item.name,
          category: item.category,
          amount: item.amount,
          description: item.description,
        })),
      })

      const totalStudents = selectedGrades.reduce((sum, grade) => {
        return sum + grade.streams.filter(s => s.selected).reduce((sSum, s) => sSum + s.studentCount, 0)
      }, 0)

      setResult({
        success: true,
        invoicesCreated: response.data.data?.invoicesCreated || totalStudents,
        totalAmount: response.data.data?.totalAmount || (totalStudents * feeItems.reduce((sum, f) => sum + f.amount, 0)),
        errors: response.data.data?.errors || [],
      })
      setCurrentStep('complete')
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to generate invoices')
      setResult({
        success: false,
        invoicesCreated: 0,
        totalAmount: 0,
        errors: [],
      })
      setCurrentStep('complete')
    } finally {
      setGenerating(false)
    }
  }

  const handleReset = () => {
    setCurrentStep('term')
    setSelectedTerm('')
    setFeeItems([])
    setSelectedGrades(grades.map(grade => ({
      gradeId: grade._id,
      gradeName: grade.name,
      streams: grade.streams.map(stream => ({
        streamId: stream._id,
        streamName: stream.name,
        studentCount: stream.studentCount || 0,
        selected: false,
      })),
      allSelected: false,
    })))
    setResult(null)
  }

  const handleViewInvoices = () => {
    window.location.href = '/schoolAdmin/invoices'
  }

  // Fee item handlers
  const handleAddFeeItem = (item: FeeItem) => setFeeItems([...feeItems, item])
  const handleRemoveFeeItem = (id: string) => setFeeItems(feeItems.filter(item => item.id !== id))
  const handleUpdateFeeItem = (id: string, updates: Partial<FeeItem>) => {
    setFeeItems(feeItems.map(item => item.id === id ? { ...item, ...updates } : item))
  }

  // Grade selection handlers
  const handleToggleGrade = (gradeId: string) => {
    setSelectedGrades(prev => prev.map(grade => {
      if (grade.gradeId !== gradeId) return grade
      const newAllSelected = !grade.allSelected
      return {
        ...grade,
        allSelected: newAllSelected,
        streams: grade.streams.map(s => ({ ...s, selected: newAllSelected })),
      }
    }))
  }

  const handleToggleStream = (gradeId: string, streamId: string) => {
    setSelectedGrades(prev => prev.map(grade => {
      if (grade.gradeId !== gradeId) return grade
      const newStreams = grade.streams.map(s =>
        s.streamId === streamId ? { ...s, selected: !s.selected } : s
      )
      return {
        ...grade,
        streams: newStreams,
        allSelected: newStreams.every(s => s.selected),
      }
    }))
  }

  const handleSelectAll = () => {
    setSelectedGrades(prev => prev.map(grade => ({
      ...grade,
      allSelected: true,
      streams: grade.streams.map(s => ({ ...s, selected: true })),
    })))
  }

  const handleClearAll = () => {
    setSelectedGrades(prev => prev.map(grade => ({
      ...grade,
      allSelected: false,
      streams: grade.streams.map(s => ({ ...s, selected: false })),
    })))
  }

  // Navigation
  const canProceed = useMemo(() => {
    switch (currentStep) {
      case 'term': return selectedTerm !== ''
      case 'fees': return feeItems.length > 0
      case 'students': return selectedGrades.some(g => g.streams.some(s => s.selected))
      case 'preview': return !generating
      default: return false
    }
  }, [currentStep, selectedTerm, feeItems, selectedGrades, generating])

  const handleNext = () => {
    const stepOrder: WizardStep[] = ['term', 'fees', 'students', 'preview', 'complete']
    const currentIndex = stepOrder.indexOf(currentStep)
    if (currentIndex < stepOrder.length - 1) {
      setCurrentStep(stepOrder[currentIndex + 1])
    }
  }

  const handleBack = () => {
    const stepOrder: WizardStep[] = ['term', 'fees', 'students', 'preview', 'complete']
    const currentIndex = stepOrder.indexOf(currentStep)
    if (currentIndex > 0) {
      setCurrentStep(stepOrder[currentIndex - 1])
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-6xl mx-auto ">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Generate Student Invoices</h1>
          <p className="text-gray-500 text-sm">Create fee invoices in a few simple steps</p>
        </div>

        <div className="flex gap-6">
          {/* Main Content */}
          <div className="flex-1">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              {/* Step Indicator */}
              {currentStep !== 'complete' && <StepIndicator currentStep={currentStep} />}

              {/* Step Content */}
              <AnimatePresence mode="wait">
                {currentStep === 'term' && (
                  <TermSelectionStep
                    key="term"
                    selectedTerm={selectedTerm}
                    academicYear={academicYear}
                    onSelectTerm={setSelectedTerm}
                    onChangeYear={setAcademicYear}
                  />
                )}
                {currentStep === 'fees' && (
                  <FeeSelectionStep
                    key="fees"
                    feeItems={feeItems}
                    onAddItem={handleAddFeeItem}
                    onRemoveItem={handleRemoveFeeItem}
                    onUpdateItem={handleUpdateFeeItem}
                  />
                )}
                {currentStep === 'students' && (
                  <StudentSelectionStep
                    key="students"
                    grades={grades}
                    selectedGrades={selectedGrades}
                    onToggleGrade={handleToggleGrade}
                    onToggleStream={handleToggleStream}
                    onSelectAll={handleSelectAll}
                    onClearAll={handleClearAll}
                    loading={gradesLoading}
                  />
                )}
                {currentStep === 'preview' && (
                  <GenerateStep
                    key="preview"
                    feeItems={feeItems}
                    selectedGrades={selectedGrades}
                    selectedTerm={selectedTerm}
                    academicYear={academicYear}
                    onGenerate={handleGenerate}
                    generating={generating}
                  />
                )}
                {currentStep === 'complete' && (
                  <CompleteStep
                    key="complete"
                    result={result}
                    onReset={handleReset}
                    onViewInvoices={handleViewInvoices}
                  />
                )}
              </AnimatePresence>

              {/* Navigation */}
              {currentStep !== 'complete' && currentStep !== 'preview' && (
                <div className="flex justify-between mt-6 pt-4 border-t border-gray-100">
                  <Button
                    variant="secondary"
                    onClick={handleBack}
                    disabled={currentStep === 'term'}
                    className={currentStep === 'term' ? 'invisible' : ''}
                  >
                    <ArrowLeftIcon className="w-4 h-4 mr-1" />
                    Back
                  </Button>
                  <Button onClick={handleNext} disabled={!canProceed}>
                    {currentStep === 'students' ? 'Review' : 'Continue'}
                    <ArrowRightIcon className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Preview Sidebar */}
          {currentStep !== 'complete' && (
            <div className="w-80 flex-shrink-0 hidden lg:block">
              <PreviewSidebar
                selectedTerm={selectedTerm}
                academicYear={academicYear}
                feeItems={feeItems}
                selectedGrades={selectedGrades}
                currentStep={currentStep}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default FeeGenerationPage
